import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import {
	Search,
	User,
	Loader2,
	Pin,
	PinOff,
	Inbox,
	Clock,
	CheckCircle,
	AlertCircle,
	UserCheck,
	Tag,
	X,
	CheckCheck,
	Unlink,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { th } from 'date-fns/locale'

import { cn } from '@/common/lib/utils'
import { Input } from '@/common/ui/input'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/common/ui/avatar'
import { Skeleton } from '@/common/ui/skeleton'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuCheckboxItem,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
	DropdownMenuLabel,
} from '@/common/ui/dropdown-menu'
import { ChatMessageType, ChatRoomStatus } from '@/schema'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuTrigger,
} from '@/common/ui/context-menu'

import {
	useQueryChatRooms,
	useMutationPinRoom,
	useMutationMarkRoomAsRead,
	useAiGlobalEnabled,
} from '../../api/hooks'
import { useQueryTags } from '../../api/hooks'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'

import type { components } from '@/schema'
import { useDebounceValue } from 'usehooks-ts'

type ChatRoomResponse = components['schemas']['ChatRoomResponse']

interface RoomListProps {
	selectedRoomId: string | null
	onSelectRoom: (roomId: string) => void
}

// Status filter - room status (INBOX, FOLLOW_UP, RESOLVED)
type RoomStatusFilter =
	| 'all'
	| ChatRoomStatus.INBOX
	| ChatRoomStatus.FOLLOW_UP
	| ChatRoomStatus.RESOLVED

// Behavior filter - special filters (needsAttention, assignedToMe)
type BehaviorFilter = 'all' | 'needsAttention' | 'assignedToMe'

interface RoomFilters {
	status: RoomStatusFilter
	behavior: BehaviorFilter
	tagIds: string[]
	hasLineId?: boolean
	noTags?: boolean
}

// Labels for status filter
const STATUS_LABELS: Record<
	RoomStatusFilter,
	{ label: string; icon: typeof Inbox }
> = {
	all: { label: 'ทั้งหมด', icon: Inbox },
	[ChatRoomStatus.INBOX]: { label: 'อินบ็อค', icon: Inbox },
	[ChatRoomStatus.FOLLOW_UP]: { label: 'ติดตาม', icon: Clock },
	[ChatRoomStatus.RESOLVED]: { label: 'แก้ไขแล้ว', icon: CheckCircle },
}

// Labels for behavior filter
const BEHAVIOR_LABELS: Record<
	BehaviorFilter,
	{ label: string; icon: typeof AlertCircle }
> = {
	all: { label: 'ทั้งหมด', icon: User },
	needsAttention: { label: 'ยังไม่อ่าน', icon: AlertCircle },
	assignedToMe: { label: 'แชทของฉัน', icon: UserCheck },
}

export function RoomList({ selectedRoomId, onSelectRoom }: RoomListProps) {
	const [search, setSearch] = useState('')
	const [debouncedSearch] = useDebounceValue(search, 500)
	const [filters, setFilters] = useState<RoomFilters>({
		status: 'all',
		behavior: 'all',
		tagIds: [],
		noTags: undefined,
	})
	const scrollContainerRef = useRef<HTMLDivElement>(null)

	const { data: profile } = useQueryProfile()
	const { data: tagsData } = useQueryTags()
	const pinRoom = useMutationPinRoom()
	const markAsRead = useMutationMarkRoomAsRead()
	const tags = useMemo(() => tagsData ?? [], [tagsData])

	const handlePinRoom = useCallback(
		(roomId: string, isPinned: boolean) => {
			pinRoom.mutate({ roomId, isPinned })
		},
		[pinRoom],
	)

	const handleMarkAsRead = useCallback(
		(roomId: string) => {
			markAsRead.mutate(roomId)
		},
		[markAsRead],
	)

	const queryParams = useMemo(() => {
		const params: Record<string, unknown> = {
			search: debouncedSearch || undefined,
			tagIds: filters.tagIds.length > 0 ? filters.tagIds : undefined,
			hasLineId: filters.hasLineId,
			noTags: filters.noTags,
			limit: 30,
		}

		// Map status filter to API params
		if (filters.status !== 'all') {
			params.status = filters.status
		}

		// Map behavior filter to API params
		switch (filters.behavior) {
			case 'needsAttention':
				params.needsAttention = true
				break
			case 'assignedToMe':
				params.assignedToMe = true
				break
		}

		return params
	}, [debouncedSearch, filters])

	const {
		data,
		isLoading,
		isError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useQueryChatRooms(queryParams)

	// Flatten pages into a single array of rooms
	const rooms = useMemo(
		() => data?.pages.flatMap((page) => page.items) ?? [],
		[data],
	)

	// Handle scroll for infinite loading
	const handleScroll = useCallback(() => {
		const container = scrollContainerRef.current
		if (!container || isFetchingNextPage || !hasNextPage) return

		const { scrollTop, scrollHeight, clientHeight } = container
		const scrollThreshold = 100 // pixels from bottom

		if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
			fetchNextPage()
		}
	}, [fetchNextPage, hasNextPage, isFetchingNextPage])

	useEffect(() => {
		const container = scrollContainerRef.current
		if (!container) return

		container.addEventListener('scroll', handleScroll)
		return () => container.removeEventListener('scroll', handleScroll)
	}, [handleScroll])

	const hasActiveFilters = useMemo(() => {
		return (
			filters.status !== 'all' ||
			filters.behavior !== 'all' ||
			filters.tagIds.length > 0 ||
			filters.hasLineId !== undefined ||
			filters.noTags !== undefined
		)
	}, [filters])

	const toggleTagFilter = useCallback((tagId: string) => {
		setFilters((prev) => ({
			...prev,
			tagIds: prev.tagIds.includes(tagId)
				? prev.tagIds.filter((id) => id !== tagId)
				: [...prev.tagIds, tagId],
		}))
	}, [])

	const clearFilters = useCallback(() => {
		setFilters({
			status: 'all',
			behavior: 'all',
			tagIds: [],
			hasLineId: undefined,
			noTags: undefined,
		})
	}, [])

	const handleStatusChange = useCallback((value: string) => {
		setFilters((prev) => ({
			...prev,
			status: value as RoomStatusFilter,
		}))
	}, [])

	const handleBehaviorChange = useCallback((value: string) => {
		setFilters((prev) => ({
			...prev,
			behavior: value as BehaviorFilter,
		}))
	}, [])

	// Get selected tags for display
	const selectedTags = useMemo(() => {
		return tags.filter((tag) => filters.tagIds.includes(tag.id))
	}, [tags, filters.tagIds])

	return (
		<div className="flex h-full flex-col overflow-hidden border-r bg-muted/30">
			{/* Header */}
			<div className="shrink-0 border-b p-4">
				{/* Search */}
				<div className="relative mb-3">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						placeholder="ค้นหาชื่อ, เบอร์โทร, หรือ Player ID..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9"
					/>
				</div>

				{/* Filters - 3 separate buttons */}
				<div className="flex flex-wrap gap-2">
					{/* Status Filter Button */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant={filters.status !== 'all' ? 'default' : 'outline'}
								size="sm"
								className="gap-1"
							>
								{(() => {
									const StatusIcon = STATUS_LABELS[filters.status].icon
									return <StatusIcon className="h-4 w-4" />
								})()}
								{STATUS_LABELS[filters.status].label}
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="start" className="w-48">
							<DropdownMenuLabel>สถานะ</DropdownMenuLabel>
							<DropdownMenuRadioGroup
								value={filters.status}
								onValueChange={handleStatusChange}
							>
								<DropdownMenuRadioItem value="all">
									<Inbox className="mr-2 h-4 w-4" />
									ทั้งหมด
								</DropdownMenuRadioItem>
								<DropdownMenuRadioItem value={ChatRoomStatus.INBOX}>
									<Inbox className="mr-2 h-4 w-4" />
									อินบ็อค
								</DropdownMenuRadioItem>
								<DropdownMenuRadioItem value={ChatRoomStatus.FOLLOW_UP}>
									<Clock className="mr-2 h-4 w-4" />
									ติดตาม
								</DropdownMenuRadioItem>
								<DropdownMenuRadioItem value={ChatRoomStatus.RESOLVED}>
									<CheckCircle className="mr-2 h-4 w-4" />
									แก้ไขแล้ว
								</DropdownMenuRadioItem>
							</DropdownMenuRadioGroup>
						</DropdownMenuContent>
					</DropdownMenu>

					{/* Behavior Filter Button */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant={filters.behavior !== 'all' ? 'default' : 'outline'}
								size="sm"
								className="gap-1"
							>
								{(() => {
									const BehaviorIcon = BEHAVIOR_LABELS[filters.behavior].icon
									return <BehaviorIcon className="h-4 w-4" />
								})()}
								{BEHAVIOR_LABELS[filters.behavior].label}
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="start" className="w-48">
							<DropdownMenuLabel>รายละเอียด</DropdownMenuLabel>
							<DropdownMenuRadioGroup
								value={filters.behavior}
								onValueChange={handleBehaviorChange}
							>
								<DropdownMenuRadioItem value="all">
									<User className="mr-2 h-4 w-4" />
									ทั้งหมด
								</DropdownMenuRadioItem>
								<DropdownMenuRadioItem value="needsAttention">
									<AlertCircle className="mr-2 h-4 w-4" />
									ยังไม่อ่าน
								</DropdownMenuRadioItem>
								<DropdownMenuRadioItem value="assignedToMe">
									<UserCheck className="mr-2 h-4 w-4" />
									แชทของฉัน
								</DropdownMenuRadioItem>
							</DropdownMenuRadioGroup>
						</DropdownMenuContent>
					</DropdownMenu>

					{/* Tags Filter Button */}
					{tags.length > 0 && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant={filters.tagIds.length > 0 ? 'default' : 'outline'}
									size="sm"
									className="gap-1"
								>
									<Tag className="h-4 w-4" />
									แท็ก
									{filters.tagIds.length > 0 && (
										<Badge
											variant="secondary"
											className="ml-1 h-5 min-w-5 rounded-full px-1 text-xs"
										>
											{filters.tagIds.length}
										</Badge>
									)}
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="start" className="w-48">
								<DropdownMenuLabel>แท็ก</DropdownMenuLabel>
								{tags.map((tag) => (
									<DropdownMenuCheckboxItem
										key={tag.id}
										checked={filters.tagIds.includes(tag.id)}
										onCheckedChange={() => toggleTagFilter(tag.id)}
									>
										<span
											className="mr-2 h-2 w-2 rounded-full"
											style={{ backgroundColor: tag.color }}
										/>
										{tag.name}
									</DropdownMenuCheckboxItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					)}

					{/* LINE Connection Filter Button */}
					<Button
						variant={filters.hasLineId === false ? 'default' : 'outline'}
						size="sm"
						className="gap-1"
						onClick={() =>
							setFilters((prev) => ({
								...prev,
								hasLineId: prev.hasLineId === false ? undefined : false,
							}))
						}
					>
						<Unlink className="h-4 w-4" />
						ยังไม่เชื่อม LINE
					</Button>

					{/* No Tags Filter Button */}
					<Button
						variant={filters.noTags === true ? 'default' : 'outline'}
						size="sm"
						className="gap-1"
						onClick={() =>
							setFilters((prev) => ({
								...prev,
								noTags: prev.noTags === true ? undefined : true,
							}))
						}
					>
						<Tag className="h-4 w-4" />
						ไม่มีแท็ก
					</Button>

					{/* Clear Filters Button */}
					{hasActiveFilters && (
						<Button
							variant="ghost"
							size="sm"
							onClick={clearFilters}
							className="gap-1 text-muted-foreground"
						>
							<X className="h-4 w-4" />
							ล้าง
						</Button>
					)}
				</div>

				{/* Active Filters Display */}
				{hasActiveFilters && (
					<div className="mt-2 flex flex-wrap gap-1">
						{selectedTags.map((tag) => (
							<Badge
								key={tag.id}
								variant="outline"
								className="gap-1 text-xs"
								style={{ borderColor: tag.color, color: tag.color }}
							>
								{tag.name}
								<button
									onClick={() => toggleTagFilter(tag.id)}
									className="ml-1 hover:opacity-70"
								>
									<X className="h-3 w-3" />
								</button>
							</Badge>
						))}
					</div>
				)}
			</div>

			{/* Room List */}
			<div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
				{isLoading ? (
					<div className="space-y-2 p-2">
						{Array.from({ length: 5 }).map((_, i) => (
							<RoomItemSkeleton key={i} />
						))}
					</div>
				) : isError ? (
					<div className="p-4 text-center text-muted-foreground">
						เกิดข้อผิดพลาดในการโหลดข้อมูล
					</div>
				) : rooms.length === 0 ? (
					<div className="p-4 text-center text-muted-foreground">ไม่พบแชท</div>
				) : (
					<div className="space-y-1 p-2">
						{rooms.map((room) => (
							<RoomItem
								key={room.id}
								room={room}
								isSelected={selectedRoomId === room.id}
								onClick={() => onSelectRoom(room.id)}
								isMyChat={room.assignedOperator?.id === profile?.id}
								onPinRoom={handlePinRoom}
								onMarkAsRead={handleMarkAsRead}
							/>
						))}

						{/* Load more indicator */}
						{isFetchingNextPage && (
							<div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
								<Loader2 className="h-4 w-4 animate-spin" />
								กำลังโหลด...
							</div>
						)}

						{/* End of list indicator */}
						{!hasNextPage && rooms.length > 0 && (
							<div className="py-4 text-center text-xs text-muted-foreground">
								แสดงทั้งหมด {rooms.length} รายการ
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

interface RoomItemProps {
	room: ChatRoomResponse
	isSelected: boolean
	onClick: () => void
	isMyChat: boolean
	onPinRoom: (roomId: string, isPinned: boolean) => void
	onMarkAsRead: (roomId: string) => void
}

function RoomItem({
	room,
	isSelected,
	onClick,
	isMyChat,
	onPinRoom,
	onMarkAsRead,
}: RoomItemProps) {
	const { aiEnabled } = useAiGlobalEnabled()
	const displayName = room.lineDisplayName || room.lineUserId
	const initials = displayName?.slice(0, 2).toUpperCase() || ''

	const lastMessagePreview = useMemo(() => {
		if (!room.lastMessage) return 'ไม่มีข้อความ'

		switch (room.lastMessage.messageType) {
			case ChatMessageType.TEXT:
				return room.lastMessage.text?.slice(0, 50) || ''
			case ChatMessageType.STICKER:
				return '[สติกเกอร์]'
			case ChatMessageType.IMAGE:
				return '[รูปภาพ]'
			case ChatMessageType.VIDEO:
				return '[วิดีโอ]'
			case ChatMessageType.AUDIO:
				return '[เสียง]'
			case ChatMessageType.FILE:
				return `[ไฟล์] ${room.lastMessage.fileName || ''}`
			case ChatMessageType.LOCATION:
				return '[ตำแหน่ง]'
			case ChatMessageType.SYSTEM:
				return `[System] ${room.lastMessage.text || ''}`
			default:
				return '[ข้อความ]'
		}
	}, [room.lastMessage])

	const timeAgo = room.lastMessageAt
		? formatDistanceToNow(new Date(room.lastMessageAt), {
				locale: th,
			})
		: null

	const needsAttention = room.unreadCount > 0 && !room.assignedOperator

	return (
		<ContextMenu>
			<ContextMenuTrigger>
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<div
								className={cn(
									'group relative flex w-full cursor-pointer items-start gap-3 rounded-lg p-3 text-left transition-colors',
									isSelected
										? 'bg-primary/10 text-primary'
										: 'hover:bg-muted/50',
									needsAttention && !isSelected && 'bg-destructive/5',
									isMyChat && !isSelected && 'border-l-2 border-l-green-500',
								)}
								onClick={onClick}
							>
								{/* Pin indicator */}
								{room.isPinned && (
									<div className="absolute right-2 top-2">
										<Pin className="h-3 w-3 text-primary" />
									</div>
								)}

								{/* Unpin button on hover */}
								{room.isPinned && (
									<Button
										variant="ghost"
										size="icon"
										className="absolute right-1 top-1 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
										onClick={(e) => {
											e.stopPropagation()
											onPinRoom(room.id, false)
										}}
									>
										<PinOff className="h-3 w-3" />
									</Button>
								)}

								<div className="relative">
									<Avatar className="h-10 w-10">
										<AvatarImage src={room.linePictureUrl} alt={displayName} />
										<AvatarFallback className="text-xs">
											{initials}
										</AvatarFallback>
									</Avatar>
									{room.unreadCount > 0 && (
										<span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-xs font-medium text-destructive-foreground">
											{room.unreadCount > 99 ? '99+' : room.unreadCount}
										</span>
									)}
									{aiEnabled ? (
										(room.adminTaskCount ?? 0) > 0 && room.unreadCount === 0 ? (
											<span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-xs font-medium text-white">
												{(room.adminTaskCount ?? 0) > 99
													? '99+'
													: room.adminTaskCount}
											</span>
										) : null
									) : null}
								</div>

								<div className="min-w-0 flex-1">
									<div className="mb-1 flex items-center justify-between gap-2">
										<span className="truncate font-medium">
											{room.player?.username
												? `${room.player.username} (${displayName})`
												: room.player?.phone
													? `${room.player.phone} (${displayName})`
													: displayName}
										</span>
										{timeAgo && (
											<span className="shrink-0 text-xs text-muted-foreground">
												{timeAgo}
											</span>
										)}
									</div>

									<p className="mb-1 truncate text-sm text-muted-foreground">
										{room.lastMessage?.direction === 'OUTGOING' &&
											room.lastMessage?.messageType !==
												ChatMessageType.SYSTEM && (
												<span className="mr-1 text-primary">คุณ:</span>
											)}
										{lastMessagePreview}
									</p>

									<div className="flex flex-wrap items-center gap-1">
										{/* Status Badge */}
										{room.status === ChatRoomStatus.FOLLOW_UP && (
											<Badge
												variant="outline"
												className="h-5 gap-1 border-amber-500 bg-amber-500/10 text-xs text-amber-600"
											>
												<Clock className="h-3 w-3" />
												ติดตาม
											</Badge>
										)}
										{room.status === ChatRoomStatus.RESOLVED && (
											<Badge
												variant="outline"
												className="h-5 gap-1 border-emerald-500 bg-emerald-500/10 text-xs text-emerald-600"
											>
												<CheckCircle className="h-3 w-3" />
												แก้ไขแล้ว
											</Badge>
										)}
										{room.assignedOperator && (
											<Badge
												variant={isMyChat ? 'default' : 'secondary'}
												className={cn(
													'h-5 text-xs',
													isMyChat && 'bg-green-600 hover:bg-green-700',
												)}
											>
												<User className="mr-1 h-3 w-3" />
												{isMyChat
													? 'แชทของคุณ'
													: room.assignedOperator.username}
											</Badge>
										)}
										{room.tags.slice(0, 2).map((tag) => (
											<Badge
												key={tag.id}
												variant="outline"
												className="h-5 text-xs"
												style={{
													borderColor: tag.color,
													color: tag.color,
												}}
											>
												{tag.name}
											</Badge>
										))}
										{room.tags.length > 2 && (
											<Badge variant="outline" className="h-5 text-xs">
												+{room.tags.length - 2}
											</Badge>
										)}
									</div>
								</div>
							</div>
						</TooltipTrigger>
						<TooltipContent side="right" className="max-w-xs">
							<div className="space-y-1">
								<p className="font-medium">{displayName}</p>
								{room.lineStatusMessage && (
									<p className="text-xs text-muted-foreground">
										{room.lineStatusMessage}
									</p>
								)}
								{room.player && (
									<p className="text-xs">
										{room.player.username
											? `ชื่อผู้ใช้: ${room.player.username}`
											: `เบอร์โทร: ${room.player.phone}`}
									</p>
								)}
							</div>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</ContextMenuTrigger>
			<ContextMenuContent>
				<ContextMenuItem
					onClick={() => onMarkAsRead(room.id)}
					disabled={room.unreadCount === 0}
				>
					<CheckCheck className="mr-2 h-4 w-4" />
					ทำเครื่องหมายว่าอ่านแล้ว
				</ContextMenuItem>
				<ContextMenuItem onClick={() => onPinRoom(room.id, !room.isPinned)}>
					{room.isPinned ? (
						<>
							<PinOff className="mr-2 h-4 w-4" />
							ยกเลิกปักหมุด
						</>
					) : (
						<>
							<Pin className="mr-2 h-4 w-4" />
							ปักหมุด
						</>
					)}
				</ContextMenuItem>
			</ContextMenuContent>
		</ContextMenu>
	)
}

function RoomItemSkeleton() {
	return (
		<div className="flex items-start gap-3 rounded-lg p-3">
			<Skeleton className="h-10 w-10 rounded-full" />
			<div className="flex-1 space-y-2">
				<Skeleton className="h-4 w-3/4" />
				<Skeleton className="h-3 w-full" />
				<Skeleton className="h-4 w-1/2" />
			</div>
		</div>
	)
}
